// The fake api's moves from another system (F-HQ-40, spec/14 §3.5), past the HQ guard in `fake-server.ts`
// (`hq:organizations`), and the venue's read-only "Moved" list (`players:read`). It reads, checks and totals an
// uploaded CSV with the same `@clavian/domain` functions the server imports with, so a test and a screenshot show the
// real numbers; an import completes over two calls, so the progress can be seen. Bodies are the contract's shapes.

import type { ImportedPlayer, MigrationRow, MigrationRun } from '@clavian/api-client';
import {
  CreateMigrationRunSchema,
  ImportedPlayerSchema,
  MigrationRunSchema,
  MigrationTicksSchema,
  UndoMigrationSchema,
  UpdateMigrationMappingSchema,
  UploadMigrationFileSchema,
} from '@clavian/contracts';
import {
  carriedOverTimeValidUntil,
  migrationIssueSeverity,
  migrationPcGroupsOf,
  readCsv,
  readMigrationRow,
  suggestColumnMapping,
  summariseMigration,
  type ValidatedMigrationRow,
  validateMigrationRows,
} from '@clavian/domain';

import type { DirectoryHelpers } from './fake-hq-directory';
import type { FakeResponse } from './fake-server';

const NOW = '2026-10-08T09:00:00.000Z';

export interface FakeMigrationVenue {
  readonly id: string;
  readonly name: string;
}

/** The venue's station classes, as the fake knows them. */
const CLASSES = [
  { id: 'scl_01k5znw4hmfay8vvr14d2pf2e1', name: 'Standard' },
  { id: 'scl_01k5znw4hmfay8vvr14d2pf2e2', name: 'VIP' },
];

interface FakeRun {
  run: MigrationRun;
  cells: string[][];
  validated: ValidatedMigrationRow[];
  /** How many import calls it has had. */
  calls: number;
}

function decodeBase64(text: string): string {
  const binary = atob(text);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new TextDecoder().decode(bytes);
}

const inr = (amount: number) => ({ amount, currency: 'INR' });

/** A few players already moved, for the venue's "Moved" list. */
export function movedPlayerFixtures(venueId: string): ImportedPlayer[] {
  const base = {
    venueId,
    sourcePlatform: 'truecafe' as const,
    joinedOn: '2021-06-01',
    lastVisitOn: '2026-10-01',
    note: null,
    banned: false,
    banReason: null,
    status: 'unclaimed' as const,
    cashTaxPaid: inr(0),
    timeValidUntil: null,
    switchedOn: '2026-10-08',
    claimWindowEndsOn: '2027-10-08',
    createdAt: NOW,
    minor: false,
    claimedAt: null,
    writtenOffAt: null,
  };
  return [
    { ...base, id: 'imp_01k5znw4hmfay8vvr14d2pf2f1', username: 'ravi_k', firstName: 'Ravi', lastName: 'Kumar', email: 'ravi.kumar@example.com', phone: '+919845012345', cash: inr(45_000), timeSeconds: 7200, timeValidUntil: '2027-04-06' },
    { ...base, id: 'imp_01k5znw4hmfay8vvr14d2pf2f2', username: 'aisha07', firstName: 'Aisha', lastName: 'Khan', email: null, phone: '+919845067890', cash: inr(125_050), cashTaxPaid: inr(125_050), timeSeconds: 19_800, timeValidUntil: '2027-04-06', joinedOn: '2022-01-15' },
    { ...base, id: 'imp_01k5znw4hmfay8vvr14d2pf2f3', username: 'neo_15', firstName: 'Neo', lastName: 'Das', email: 'neo.das@example.com', phone: null, cash: inr(12_000), timeSeconds: 0, minor: true, joinedOn: '2025-05-20' },
    { ...base, id: 'imp_01k5znw4hmfay8vvr14d2pf2f4', username: 'whale', firstName: 'Big', lastName: 'Spender', email: 'whale@example.com', phone: null, cash: inr(2_500_000), timeSeconds: 0, joinedOn: '2020-01-01' },
    { ...base, id: 'imp_01k5znw4hmfay8vvr14d2pf2f5', username: 'owes', firstName: 'Debt', lastName: 'Owed', email: 'owes@example.com', phone: null, cash: inr(0), timeSeconds: 0, banned: true, banReason: 'Fighting', status: 'claimed' as const, claimedAt: NOW, claimVia: 'counter' as const },
  ].map((player) => ImportedPlayerSchema.parse(player) as ImportedPlayer);
}

export function createFakeHqMigrations(options: {
  readonly organizationId: string;
  readonly venues: () => readonly FakeMigrationVenue[];
  readonly moved?: readonly ImportedPlayer[];
  /** The venue's tax mode on any switch-over day: `regular` unless a test says the venue is not registered. */
  readonly venueTaxMode?: 'regular' | 'composition' | 'unregistered';
}) {
  const venueTaxMode = options.venueTaxMode ?? 'regular';
  const gstQuestion = venueTaxMode !== 'unregistered';
  const runs = new Map<string, FakeRun>();
  const imported: ImportedPlayer[] = [...(options.moved ?? [])];
  let sequence = 0;

  function summarise(entry: FakeRun): void {
    const run = entry.run;
    const mapping = run.mapping;
    if (mapping === null || run.switchOn === null) return;
    const records = entry.cells.map((cells, index) => readMigrationRow(cells, index + 2, mapping.columns, { currency: 'INR', region: 'IN', moneyUnit: mapping.moneyUnit, timeUnit: mapping.timeUnit, dateOrder: mapping.dateOrder }));
    const classMap = run.classMap ?? { groups: [], fallback: [] };
    entry.validated = validateMigrationRows(records, {
      today: run.switchOn as never,
      minorAge: 18,
      maxWalletBalanceMinor: 2_000_000,
      reviewAmountMinor: 500_000,
      reviewSeconds: 72_000,
      classMap: { groups: Object.fromEntries(classMap.groups.map((group) => [group.group, [...group.stationClassIds]])), fallback: [...classMap.fallback] },
      isBlockedEmail: (email) => email.endsWith('@ggleap.com'),
      existingAccountEmails: new Set(['known.person@example.com']),
      alreadyImported: new Set(),
    });
    const summary = summariseMigration(entry.validated, { gstPaidAtTopUp: gstQuestion && run.gstPaidAtTopUp === true });
    const ticked = new Set(run.ticked);
    const ready = mapping.columns.username !== undefined && (summary.cashMinor === 0 || !gstQuestion || run.gstPaidAtTopUp !== null) && summary.players > 0;
    entry.run = {
      ...run,
      status: run.status === 'draft' || run.status === 'ready' ? (ready ? 'ready' : 'draft') : run.status,
      summary: {
        rows: summary.rows,
        byStatus: { ...summary.byStatus },
        players: summary.players,
        cash: inr(summary.cashMinor),
        cashTaxPaid: inr(summary.cashTaxPaidMinor),
        timeSeconds: summary.timeSeconds,
        timePlayers: summary.timePlayers,
        bonusDropped: inr(summary.bonusDroppedMinor),
        owedNotCarried: inr(summary.owedNotCarriedMinor),
        minors: summary.minors,
        claimableByEmail: summary.claimableByEmail,
        counterOnly: summary.counterOnly,
        existingAccounts: summary.existingAccounts,
        largeBalances: summary.largeBalances.map((balance) => ({ ...balance, cash: inr(balance.cashMinor), ticked: ticked.has(balance.sourceKey), cashMinor: undefined })).map(({ cashMinor: _drop, ...rest }) => rest),
        needsTick: summary.needsTick,
        issueCounts: { ...summary.issueCounts } as Record<string, number>,
      },
      progress: { toImport: summary.players, imported: 0, skipped: summary.byStatus.skipped, remaining: summary.players },
    };
  }

  function rowsOf(entry: FakeRun): MigrationRow[] {
    return entry.validated.map((row) => ({
      rowNumber: row.record.rowNumber,
      sourceKey: row.record.sourceKey,
      username: row.record.username,
      name: [row.record.firstName, row.record.lastName].filter((part) => part !== null).join(' ') || null,
      email: row.record.email,
      phone: row.record.phone,
      cash: inr(row.record.cashMinor),
      bonus: inr(row.record.bonusMinor),
      timeSeconds: row.record.timeSeconds,
      minor: row.minor,
      status: entry.run.status === 'completed' && (row.status === 'ok' || row.status === 'warning') ? 'imported' : row.status,
      issues: row.issues.map((issue) => ({ code: issue.code, severity: migrationIssueSeverity(issue.code), ...(issue.field === undefined ? {} : { field: issue.field }) })),
      importedPlayerId: entry.run.status === 'completed' && (row.status === 'ok' || row.status === 'warning') ? `imp_01k5znw4hmfay8vvr14d2p${String(row.record.rowNumber).padStart(4, '0')}` : null,
    })) as MigrationRow[];
  }

  function handle(helpers: DirectoryHelpers, method: string, pathname: string, searchParams: URLSearchParams, input: Record<string, unknown>): FakeResponse | undefined {
    const { json, problem } = helpers;
    const match = /^\/v1\/hq\/organizations\/([^/]+)\/migrations(?:\/([^/]+)(?:\/(file|mapping|rows|ticks|import|undo|report|moved-email))?)?$/.exec(pathname);
    if (match === null) return undefined;
    const [, organizationId, migrationId, action] = match;
    if (organizationId !== options.organizationId) return problem(404, 'not_found', 'No such organisation.');

    if (migrationId === undefined) {
      if (method === 'GET') {
        const data = [...runs.values()].map((entry) => entry.run).reverse();
        return json(200, { data, page: { cursor: null, hasMore: false, count: data.length } });
      }
      const parsed = CreateMigrationRunSchema.safeParse(input);
      if (!parsed.success) return problem(400, 'validation_failed', 'Validation failed');
      const venue = options.venues().find((one) => one.id === parsed.data.venueId);
      if (venue === undefined) return problem(404, 'not_found', 'No such venue in this organisation.');
      sequence += 1;
      const id = `mig_01k5znw4hmfay8vvr14d2pf${String(sequence).padStart(3, '0')}`;
      const run = MigrationRunSchema.parse({
        id,
        venueId: venue.id,
        venueName: venue.name,
        sourcePlatform: parsed.data.sourcePlatform,
        template: 'clavian_csv',
        status: 'draft',
        file: null,
        files: [],
        headers: [],
        sampleRows: [],
        suggestedMapping: null,
        mapping: null,
        pcGroups: [],
        classMap: null,
        stationClasses: CLASSES,
        gstPaidAtTopUp: null,
        venueTaxMode,
        gstQuestion,
        switchOn: null,
        summary: null,
        ticked: [],
        progress: { toImport: 0, imported: 0, skipped: 0, remaining: 0 },
        posted: null,
        walletEnabled: true,
        lastError: null,
        createdAt: NOW,
        importStartedAt: null,
        completedAt: null,
        undoneAt: null,
        claimWindowEndsOn: null,
        movedEmail: { round: 0, requestedAt: null, sent: 0, pending: 0 },
      }) as MigrationRun;
      runs.set(id, { run, cells: [], validated: [], calls: 0 });
      return json(201, run);
    }

    const entry = runs.get(migrationId);
    if (entry === undefined) return problem(404, 'not_found', 'No such migration.');
    const editable = entry.run.status === 'draft' || entry.run.status === 'ready';

    switch (action) {
      case undefined:
        return json(200, entry.run);
      case 'file': {
        if (!editable) return problem(409, 'conflict', 'The import has begun.');
        const parsed = UploadMigrationFileSchema.safeParse(input);
        if (!parsed.success) return problem(400, 'validation_failed', 'Validation failed');
        let rows: string[][];
        try {
          rows = readCsv(decodeBase64(parsed.data.contentBase64));
        } catch {
          return problem(422, 'migration_file_unreadable', 'The export could not be read', { detail: 'A quote is never closed.' });
        }
        const [header = [], ...body] = rows;
        entry.cells = body;
        entry.run = {
          ...entry.run,
          file: { name: parsed.data.fileName, format: 'csv', sha256: 'a'.repeat(64), bytes: parsed.data.contentBase64.length, rows: body.length, uploadedAt: NOW },
          files: [...entry.run.files, { name: parsed.data.fileName, format: 'csv', sha256: 'a'.repeat(64), bytes: parsed.data.contentBase64.length, rows: body.length, uploadedAt: NOW }],
          headers: header,
          sampleRows: body.slice(0, 5),
          suggestedMapping: { columns: suggestColumnMapping(header), moneyUnit: 'major', timeUnit: 'hms', dateOrder: 'dmy' },
          mapping: null,
          summary: null,
          status: 'draft',
        };
        return json(200, entry.run);
      }
      case 'mapping': {
        if (!editable) return problem(409, 'conflict', 'The import has begun.');
        const parsed = UpdateMigrationMappingSchema.safeParse(input);
        if (!parsed.success) return problem(400, 'validation_failed', 'Validation failed');
        if (!gstQuestion && parsed.data.gstPaidAtTopUp === true) return problem(400, 'validation_failed', 'Validation failed', { detail: 'This venue is not registered for GST.' });
        const mapping = parsed.data.mapping;
        const groupIndex = mapping.columns.timePcGroups;
        entry.run = {
          ...entry.run,
          mapping,
          classMap: parsed.data.classMap,
          gstPaidAtTopUp: gstQuestion ? parsed.data.gstPaidAtTopUp : false,
          switchOn: parsed.data.switchOn,
          pcGroups: migrationPcGroupsOf(entry.cells.map((cells) => ({ timePcGroups: groupIndex === undefined ? [] : (cells[groupIndex] ?? '').split(/[;|,]/).map((group) => group.trim()).filter((group) => group !== '') }))),
        };
        summarise(entry);
        return json(200, entry.run);
      }
      case 'rows': {
        const status = searchParams.get('status');
        const limit = Number(searchParams.get('limit') ?? 25);
        const data = rowsOf(entry).filter((row) => status === null || row.status === status);
        return json(200, { data: data.slice(0, limit), page: { cursor: data.length > limit ? String(limit) : null, hasMore: data.length > limit, count: Math.min(limit, data.length) } });
      }
      case 'ticks': {
        const parsed = MigrationTicksSchema.safeParse(input);
        if (!parsed.success) return problem(400, 'validation_failed', 'Validation failed');
        entry.run = { ...entry.run, ticked: [...parsed.data.sourceKeys] };
        summarise(entry);
        return json(200, entry.run);
      }
      case 'import': {
        const summary = entry.run.summary;
        if (summary === null || (entry.run.status !== 'ready' && entry.run.status !== 'importing' && entry.run.status !== 'completed')) {
          return problem(409, 'conflict', 'Not ready.');
        }
        if (entry.run.status === 'ready' && summary.largeBalances.some((balance) => balance.needsTick && !entry.run.ticked.includes(balance.sourceKey))) {
          return problem(409, 'migration_ticks_missing', 'Large balances are not ticked', { detail: 'A balance above the wallet’s maximum is not ticked.' });
        }
        entry.calls += 1;
        const half = Math.ceil(summary.players / 2);
        const done = entry.calls >= 2 || entry.run.status === 'completed';
        entry.run = {
          ...entry.run,
          status: done ? 'completed' : 'importing',
          importStartedAt: entry.run.importStartedAt ?? NOW,
          completedAt: done ? NOW : null,
          progress: { toImport: summary.players, imported: done ? summary.players : half, skipped: summary.byStatus.skipped, remaining: done ? 0 : summary.players - half },
          posted: { players: done ? summary.players : half, cash: done ? summary.cash : inr(0), cashTaxPaid: done ? summary.cashTaxPaid : inr(0), timeSeconds: done ? summary.timeSeconds : 0, undone: 0, claimed: 0 },
          claimWindowEndsOn: entry.run.switchOn === null ? null : carriedOverTimeValidUntil(entry.run.switchOn, 365),
        };
        return json(200, entry.run);
      }
      case 'undo': {
        const parsed = UndoMigrationSchema.safeParse(input);
        if (!parsed.success) return problem(400, 'validation_failed', 'Validation failed');
        const posted = entry.run.posted;
        const undone = parsed.data.importedPlayerId === undefined ? (posted?.players ?? 0) : 1;
        entry.run = {
          ...entry.run,
          ...(parsed.data.importedPlayerId === undefined ? { status: 'undone' as const, undoneAt: NOW } : {}),
          posted: posted === null ? null : { ...posted, players: posted.players - undone, undone: posted.undone + undone, ...(parsed.data.importedPlayerId === undefined ? { cash: inr(0), cashTaxPaid: inr(0), timeSeconds: 0 } : {}) },
        };
        return json(200, { undone, claimed: [], run: entry.run });
      }
      case 'moved-email': {
        if (entry.run.status !== 'completed') return problem(409, 'conflict', 'The import has not completed.');
        const resend = (input as { resend?: unknown } | undefined)?.resend === true;
        const waiting = Math.max(0, (entry.run.posted?.players ?? 0) - (entry.run.posted?.claimed ?? 0));
        const round = resend || entry.run.movedEmail.round === 0 ? entry.run.movedEmail.round + 1 : entry.run.movedEmail.round;
        entry.run = { ...entry.run, movedEmail: { round, requestedAt: NOW, sent: waiting, pending: 0 } };
        return json(200, entry.run);
      }
      case 'report':
        return { status: 200, headers: { 'content-type': 'text/csv; charset=utf-8' }, body: 'row,source_id,username\r\n2,1001,ravi_k\r\n' };
      default:
        return problem(404, 'not_found', 'Not found');
    }
  }

  /** Profiles at the end of their window, for the owner's write-off (F-IAM-13). */
  const writeOffs = [
    { id: 'imp_01k5znw4hmfay8vvr14d2pf2g1', venueId: options.venues()[0]?.id ?? '', sourcePlatform: 'truecafe' as const, username: null, state: 'ended' as const, claimWindowEndsOn: '2027-10-08', cash: inr(48_000), cashTaxPaid: inr(0), timeSeconds: 0 },
    { id: 'imp_01k5znw4hmfay8vvr14d2pf2g2', venueId: options.venues()[0]?.id ?? '', sourcePlatform: 'truecafe' as const, username: null, state: 'ended' as const, claimWindowEndsOn: '2027-10-08', cash: inr(12_550), cashTaxPaid: inr(12_550), timeSeconds: 0 },
    { id: 'imp_01k5znw4hmfay8vvr14d2pf2g3', venueId: options.venues()[0]?.id ?? '', sourcePlatform: 'truecafe' as const, username: 'priya_m', state: 'expiring' as const, claimWindowEndsOn: '2027-10-30', cash: inr(30_000), cashTaxPaid: inr(0), timeSeconds: 3600 },
  ];
  let writtenOff = new Set<string>();

  /**
   * The venue's "Moved" list (`GET …/imported-players`, `players:read`), and claiming at the counter and the owner's
   * write-off (F-IAM-13): a code to an email, the approvers, the claim — above ₹2,000 only with a manager's PIN
   * (`481593` in the fake), a minor's only with a guardian — and the write-off list and its approval.
   */
  function handleOrganization(
    method: string,
    pathname: string,
    searchParams: URLSearchParams,
    permissions: ReadonlySet<string>,
    json: DirectoryHelpers['json'],
    problem: DirectoryHelpers['problem'],
    input?: unknown,
  ): FakeResponse | undefined {
    const base = `/v1/organizations/${options.organizationId}/imported-players`;
    if (!pathname.startsWith(base)) return undefined;
    const rest = pathname.slice(base.length);
    if (rest === '/write-offs') {
      if (!permissions.has('credit:write_off')) return problem(403, 'forbidden', 'Not allowed');
      const live = writeOffs.filter((item) => !writtenOff.has(item.id));
      if (method === 'GET') {
        const total = (state: string) => inr(live.filter((item) => item.state === state).reduce((sum, item) => sum + item.cash.amount, 0));
        return json(200, { data: live, endedTotal: total('ended'), expiringTotal: total('expiring') });
      }
      if (method === 'POST') {
        const ids = ((input as { importedPlayerIds?: string[] } | undefined)?.importedPlayerIds ?? []).filter((id) => live.some((item) => item.id === id && item.state === 'ended'));
        if (ids.length === 0) return problem(409, 'conflict', 'Nothing to write off.');
        const amount = live.filter((item) => ids.includes(item.id)).reduce((sum, item) => sum + item.cash.amount, 0);
        writtenOff = new Set([...writtenOff, ...ids]);
        return json(200, { writtenOff: ids.length, amount: inr(amount), at: NOW });
      }
      return undefined;
    }
    const claim = /^\/([^/]+)\/claim(\/email-code|\/approvers)?$/.exec(rest);
    if (claim !== null) {
      if (!permissions.has('wallet:topup')) return problem(403, 'forbidden', 'Not allowed');
      const player = imported.find((one) => one.id === claim[1]);
      if (player === undefined) return problem(404, 'not_found', 'No such moved player here.');
      if (claim[2] === '/approvers') return json(200, { data: [{ memberId: 'mem_01k5znw4hmfay8vvr14d2pf2h1', name: 'Priya Menon' }] });
      if (player.status !== 'unclaimed') return problem(409, 'conflict', 'This profile is no longer waiting to be claimed.');
      if (claim[2] === '/email-code') return json(202, { expiresInSeconds: 600, resendAfterSeconds: 30 });
      const body = (input ?? {}) as { playerId?: string; email?: string; code?: string; guardian?: unknown; approval?: { memberId: string; pin: string } };
      if (player.minor && body.guardian === undefined) return problem(400, 'validation_failed', 'A guardian is needed', { detail: 'An under-18’s profile is claimed with a parent or guardian present.' });
      if (player.cash.amount > 200_000 && body.approval === undefined) return problem(403, 'carried_over_approval_required', 'A manager approves this claim');
      if (body.approval !== undefined && body.approval.pin !== '481593') return problem(400, 'staff_pin_incorrect', 'Incorrect PIN');
      if (body.email !== undefined && body.code !== '123456') return problem(400, 'otp_invalid', 'That code is not right');
      const index = imported.indexOf(player);
      const claimed = { ...player, status: 'claimed' as const, claimedAt: NOW, claimVia: 'counter' as const };
      imported[index] = claimed;
      return json(200, {
        importedPlayer: claimed,
        player: { id: body.playerId ?? 'ply_01k5znw4hmfay8vvr14d2pf2j1' },
        createdAccount: body.playerId === undefined,
        carriedOver: { profiles: 1, sourcePlatform: player.sourcePlatform, cash: player.cash, cashTaxPaid: player.cashTaxPaid, timeSeconds: player.timeSeconds, timeValidUntil: player.timeValidUntil },
      });
    }
    if (rest !== '' || method !== 'GET') return undefined;
    if (!permissions.has('players:read')) return problem(403, 'forbidden', 'Not allowed');
    const q = (searchParams.get('q') ?? '').trim().toLowerCase();
    const contact = permissions.has('players:read_contact');
    const data = imported
      .filter((player) => q === '' || player.username.toLowerCase().includes(q) || `${player.firstName ?? ''} ${player.lastName ?? ''}`.toLowerCase().includes(q) || player.email === q || player.phone === q)
      .map((player) => (contact ? player : { ...player, email: player.email === null ? null : `${player.email.slice(0, 1)}•••@${player.email.split('@')[1] ?? ''}`, phone: player.phone === null ? null : `${player.phone.slice(0, 3)} ••••• ${player.phone.slice(-3)}` }));
    return json(200, { data, page: { cursor: null, hasMore: false, count: data.length } });
  }

  return { handle, handleOrganization, runs, imported };
}
